import { lazy } from 'react';
import { useEffect } from 'react';

import VMSLogin from './pages/ECMLogin';
import Home from './pages/Home';
import Login from './pages/Login';
import NotFound from './pages/NotFound';
import WorkshopLogin from './pages/WorkshopLogin';

const getReloadAttemptedKey = (path) => `reload_attempted_for_${path}`;

const DefaultListView = ({ componentPath }) => {
    useEffect(() => {
        const reloadKey = getReloadAttemptedKey(componentPath);
        if (!sessionStorage.getItem(reloadKey)) {
            console.warn(`Fallback for ${componentPath}: Attempting one-time reload.`);
            sessionStorage.setItem(reloadKey, 'true');
            window.location.reload();
        }
    }, [componentPath]);

    return <div>Failed to load. Fallback List View for component at: {componentPath}. (Reload previously attempted if this message persists)</div>;
};

const DefaultEditView = ({ componentPath }) => {
    useEffect(() => {
        const reloadKey = getReloadAttemptedKey(componentPath);
        if (!sessionStorage.getItem(reloadKey)) {
            console.warn(`Fallback for ${componentPath}: Attempting one-time reload.`);
            sessionStorage.setItem(reloadKey, 'true');
            window.location.reload();
        }
    }, [componentPath]);

    return <div>Failed to load. Fallback Edit View for component at: {componentPath}. (Reload previously attempted if this message persists)</div>;
};

const DefaultAddView = ({ componentPath }) => {
    useEffect(() => {
        const reloadKey = getReloadAttemptedKey(componentPath);
        if (!sessionStorage.getItem(reloadKey)) {
            console.warn(`Fallback for ${componentPath}: Attempting one-time reload.`);
            sessionStorage.setItem(reloadKey, 'true');
            window.location.reload();
        }
    }, [componentPath]);

    return <div>Failed to load. Fallback Add View for component at: {componentPath}. (Reload previously attempted if this message persists)</div>;
};

const loadComponent = (path, fallbackType = 'list') => {
    let BaseFallbackComponent; // This will be one of DefaultListView, DefaultEditView, etc.
    switch (fallbackType) {
        case 'add':
            BaseFallbackComponent = DefaultAddView;
            break;
        case 'edit':
            BaseFallbackComponent = DefaultEditView;
            break;
        case 'list':
        default:
            BaseFallbackComponent = DefaultListView;
            break;
    }

    return lazy(() =>
        import(/* webpackChunkName: "[request]" */ `${path}`)
            .then((module) => {
                const reloadKey = getReloadAttemptedKey(path);
                if (sessionStorage.getItem(reloadKey)) {
                    sessionStorage.removeItem(reloadKey);
                }
                return { default: module.default };
            })
            .catch((error) => {
                console.error(`Failed to import component at "${path}". Rendering fallback.`, error);
                const FallbackWrapper = () => <BaseFallbackComponent componentPath={path} />;
                return { default: FallbackWrapper };
            })
    );
};
const routeConfig = [
    // --- Routes outside MainLayout ---
    // { path: '/vms-login', element: VMSLogin, layout: 'none', pageName: 'VMS Login' },
    { path: '/login', element: Login, layout: 'none', pageName: 'Login' },
    {
        path: '/service',
        element: WorkshopLogin,
        layout: 'none',
        pageName: 'Workshop Login'
    },
    {
        path: '/service/work-tickets',
        element: loadComponent('./pages/WorkTickets', 'list'),
        layout: 'workshop',
        pageName: 'Work Tickets',
        auth: { permission: 'view_workshop_work_tickets' }
    },
    {
        path: '/service/work-tickets/:id',
        element: loadComponent('./pages/WorkTickets/Detail', 'list'),
        layout: 'workshop',
        pageName: 'Work Ticket Detail',
        auth: { permission: 'view_workshop_work_tickets' }
    },

    // --- Routes inside MainLayout ---
    { path: '/home', element: Home, layout: 'main', pageName: 'Home' },
    {
        path: '/api-docs',
        element: Home,
        layout: 'main',
        iframeUrl: `${process.env.REACT_APP_API_URL}/api-docs/`,
        auth: { permission: 'view_api_documentation' }
    },

    // DVSA Earned Recognition
    {
        path: '/compliance/earned-recognition',
        element: loadComponent('./pages/Compliance/EarnedRecognition', 'list'),
        layout: 'main',
        pageName: 'DVSA Earned Recognition',
        auth: { permission: 'er_view' }
    },
    // Service Calendar
    {
        path: '/service-calendar',
        element: loadComponent('./pages/ServiceCalendar', 'list'),
        layout: 'main',
        pageName: 'Service Calendar',
        auth: { permission: 'view_service_calendar' }
    },

    // Daily Service
    {
        path: '/daily-service',
        element: loadComponent('./pages/DailyService', 'list'),
        layout: 'main',
        pageName: 'Daily Service',
        auth: { permission: 'view_daily_service' }
    },

    // --- Scheduled Services ---
    // {
    //     path: '/scheduled-services',
    //     element: loadComponent('./pages/ScheduledServices', 'list'),
    //     layout: 'main',
    //     pageName: 'Scheduled Services',
    //     auth: { }
    // },
    {
        path: '/scheduled-services/add',
        element: loadComponent('./pages/ScheduledServices/Add', 'add'),
        layout: 'main',
        pageName: 'New Scheduled Service',
        auth: { permission: 'manage_scheduled_services' }
    },
    {
        path: '/unscheduled-services/add',
        element: loadComponent('./pages/UnscheduledServices/Add', 'add'),
        layout: 'main',
        pageName: 'New Unscheduled Service Form',
        auth: { permission: 'manage_unscheduled_services' }
    },

    // {
    //     path: '/defect-tickets',
    //     element: loadComponent('./pages/DefectTickets/index', 'list'),
    //     layout: 'main',
    //     pageName: 'Defect Tickets',
    //     auth: {permission: 'read_defect_tickets' }
    // },
    {
        path: '/defect-tickets/add',
        element: loadComponent('./pages/DefectTickets/Add', 'add'),
        layout: 'main',
        pageName: 'Add Defect Ticket',
        auth: { permission: 'manage_defect_tickets' }
    },

    // --- Work Tickets ---
    {
        path: '/work-tickets',
        element: loadComponent('./pages/WorkTickets', 'list'),
        layout: 'main',
        pageName: 'Work Tickets',
        auth: { permission: 'view_work_tickets' }
    },
    {
        path: '/work-tickets/:id',
        element: loadComponent('./pages/WorkTickets/Detail', 'list'),
        layout: 'main',
        pageName: 'Work Ticket Detail',
        auth: { permission: 'view_work_tickets' }
    },

    // --- Service Tools ---
    {
        path: '/service-tools/service-types',
        element: loadComponent('./pages/ServiceTools/ServiceTypes', 'list'),
        layout: 'main',
        pageName: 'Service Types',
        auth: { permission: 'view_service_types' }
    },
    {
        path: '/service-tools/service-tasks',
        element: loadComponent('./pages/ServiceTools/ServiceTasks', 'list'),
        layout: 'main',
        pageName: 'Service Tasks',
        auth: { permission: 'view_service_types' }
    },
    {
        path: '/service-tools/service-task-form',
        element: loadComponent('./pages/ServiceTools/ServiceTasks/ServiceTaskForm', 'edit'),
        layout: 'main',
        pageName: 'Service Task Form',
        auth: { permission: 'manage_service_types' }
    },

    // Service Tree
    {
        path: '/service-tools/service-tree',
        element: loadComponent('./pages/ServiceTools/ServiceTree', 'list'),
        layout: 'main',
        pageName: 'Service Tree',
        auth: { permission: 'view_service_tree' }
    },
    {
        path: '/service-tools/service-tree/static',
        element: loadComponent('./pages/ServiceTools/ServiceTree', 'list'),
        layout: 'main',
        pageName: 'Service Tree',
        auth: { permission: 'view_service_tree' }
    },
    {
        path: '/service-tools/service-tree/dynamic',
        element: loadComponent('./pages/ServiceTools/ServiceTree', 'list'),
        layout: 'main',
        pageName: 'Service Tree',
        auth: { permission: 'view_service_tree' }
    },
    {
        path: '/service-tools/service-tree/static/:id',
        element: loadComponent('./pages/ServiceTools/ServiceTree', 'list'),
        layout: 'main',
        pageName: 'Service Tree',
        auth: { permission: 'view_service_tree' }
    },
    {
        path: '/service-tools/service-tree/dynamic/:id',
        element: loadComponent('./pages/ServiceTools/ServiceTree', 'list'),
        layout: 'main',
        pageName: 'Service Tree',
        auth: { permission: 'view_service_tree' }
    },
    {
        path: '/service-tools/serviceForm',
        element: loadComponent('./pages/ServiceTools/ServiceTypes/ServiceForm', 'edit'),
        layout: 'main',
        pageName: 'Service Form',
        auth: { permission: 'manage_service_types' }
    },
    {
        path: '/service-tools/taskList',
        element: loadComponent('./pages/ServiceTools/ServiceTypes/TaskTypeList'),
        layout: 'main',
        pageName: 'Task List',
        auth: { permission: 'view_task_types' }
    },
    {
        path: '/service-tools/taskForm',
        element: loadComponent('./pages/ServiceTools/ServiceTypes/TaskForm', 'edit'),
        layout: 'main',
        pageName: 'Task Form',
        auth: { permission: 'manage_task_types' }
    },
    {
        path: '/service-tools/subtaskList',
        element: loadComponent('./pages/ServiceTools/ServiceTypes/SubTaskList'),
        layout: 'main',
        pageName: 'Subtask List',
        auth: { permission: 'view_subtask_types' }
    },
    {
        path: '/service-tools/subtaskForm',
        element: loadComponent('./pages/ServiceTools/ServiceTypes/SubTaskForm', 'edit'),
        layout: 'main',
        pageName: 'Subtask Form',
        auth: { permission: 'manage_subtask_types' }
    },
    {
        path: '/service-tools/workFailList',
        element: loadComponent('./pages/ServiceTools/ServiceTypes/WorkStaticList'),
        layout: 'main',
        pageName: 'WorkFail List',
        auth: { permission: 'view_workfail_types' }
    },
    {
        path: '/service-tools/workFailForm',
        element: loadComponent('./pages/ServiceTools/ServiceTypes/WorkFailForm', 'edit'),
        layout: 'main',
        pageName: 'WorkFail Form',
        auth: { permission: 'manage_workfail_types' }
    },

    // --- Tools ---
    {
        path: '/tools/vehicles',
        element: loadComponent('./pages/Tools/Vehicles/index', 'list'),
        layout: 'main',
        pageName: 'Vehicles'
        // auth: { permission: 'view_make_model' }
    },
    {
        path: '/tools/vehicles/add',
        element: loadComponent('./pages/Tools/Vehicles/Add', 'add'),
        layout: 'main',
        pageName: 'Add Vehicle'
        // auth: { permission: 'view_make_model' }
    },
    {
        path: '/tools/vehicles/edit/:id',
        element: loadComponent('./pages/Tools/Vehicles/Edit', 'edit'),
        layout: 'main',
        pageName: 'Edit Vehicle'
        // auth: { permission: 'view_make_model' }
    },
    {
        path: '/tools/vehicle-tasks',
        element: loadComponent('./pages/Tools/VehicleTasks/index', 'list'),
        layout: 'main',
        pageName: 'Location Zone',
        auth: { permission: 'view_location_zone' }
    },
    {
        path: '/tools/vehicle-tasks/add',
        element: loadComponent('./pages/Tools/VehicleTasks/Add', 'add'),
        layout: 'main',
        pageName: 'Add Location Zone',
        auth: { permission: 'manage_location_zone' }
    },
    {
        path: '/tools/vehicle-tasks/edit/:id',
        element: loadComponent('./pages/Tools/VehicleTasks/Edit', 'edit'),
        layout: 'main',
        pageName: 'Edit Location Zone',
        auth: { permission: 'manage_location_zone' }
    },
    {
        path: '/tools/manage-users',
        element: loadComponent('./pages/Tools/ManageUsers', 'list'),
        layout: 'main',
        pageName: 'Manage Users',
        auth: { permission: 'view_system_users' }
    },
    {
        path: '/tools/manage-users/add',
        element: loadComponent('./pages/Tools/ManageUsers/Add', 'add'),
        layout: 'main',
        pageName: 'Add User',
        auth: { permission: 'manage_system_users' }
    },

    {
        path: '/tools/manage-users/import',
        element: loadComponent('./pages/Tools/ManageUsers/Import', 'edit'),
        layout: 'main',
        pageName: 'Import User',
        auth: { permission: 'manage_system_users' }
    },
    {
        path: '/tools/manage-users/import/configure-access',
        element: loadComponent('./pages/Tools/ManageUsers/ConfigureUserPermissions', 'edit'),
        layout: 'main',
        pageName: 'Configure User',
        auth: { permission: 'manage_system_users' }
    },
    {
        path: '/tools/manage-users/permissions',
        element: loadComponent('./pages/Tools/ManageUsers/Permissions', 'edit'),
        layout: 'main',
        pageName: 'Manage Permissions',
        auth: { permission: 'manage_system_users' }
    },
    {
        path: '/tools/manage-users/permissions/:id',
        element: loadComponent('./pages/Tools/ManageUsers/EditPermissions', 'edit'),
        layout: 'main',
        pageName: 'Edit User Permissions',
        auth: { permission: 'manage_system_users' }
    },
    {
        path: '/tools/manage-users/edit/:id',
        element: loadComponent('./pages/Tools/ManageUsers/Edit', 'edit'),
        layout: 'main',
        pageName: 'Edit User',
        auth: { permission: 'manage_system_users' }
    },
    {
        path: '/tools/audit-log',
        element: loadComponent('./pages/Tools/AuditLog/index', 'list'),
        layout: 'main',
        pageName: 'Audit Log',
        auth: { permission: 'view_audit_log' }
    },
    {
        path: '/tools/color-picker',
        element: loadComponent('./pages/Tools/ColorPicker/index', 'list'),
        layout: 'main',
        pageName: 'Color Picker',
        auth: { permission: 'view_color_picker' }
    },
    {
        path: '/tools/styleguide',
        element: loadComponent('./pages/Tools/Styleguide/index', 'list'),
        layout: 'main',
        pageName: 'Style Guide',
        auth: { permission: 'view_style_guide' },
        iframeUrl: `${process.env.REACT_APP_API_URL}/ph/styleguide/`
    },

    // --- Settings ---
    // DropDown
    {
        path: '/settings/dropdown',
        element: loadComponent('./pages/Settings/Dropdown/index', 'list'),
        layout: 'main',
        pageName: 'Drop Down',
        auth: { permission: 'view_system_dropdowns' }
    },
    {
        path: '/settings/dropdown/edit',
        element: loadComponent('./pages/Settings/Dropdown/Edit', 'edit'),
        layout: 'main',
        pageName: 'Edit Drop Down',
        auth: { permission: 'manage_system_dropdowns' }
    },
    {
        path: '/settings/dropdown/add',
        element: loadComponent('./pages/Settings/Dropdown/Add', 'add'),
        layout: 'main',
        pageName: 'Add Drop Down',
        auth: { permission: 'manage_system_dropdowns' }
    },

    // Translation
    {
        path: '/settings/translation',
        element: loadComponent('./pages/Settings/Translation/index', 'list'),
        layout: 'main',
        pageName: 'Translation',
        auth: { permission: 'view_translations' }
    },
    {
        path: '/settings/translation/add',
        element: loadComponent('./pages/Settings/Translation/Add', 'add'),
        layout: 'main',
        pageName: 'Add Translation',
        auth: { permission: 'manage_translations' }
    },
    {
        path: '/settings/translation/edit/:id',
        element: loadComponent('./pages/Settings/Translation/Edit', 'edit'),
        layout: 'main',
        pageName: 'Edit Translation',
        auth: { permission: 'manage_translations' }
    },

    // Menu Setup
    {
        path: '/settings/menu-setup',
        element: loadComponent('./pages/Settings/MenuSetup/index', 'list'),
        layout: 'main',
        pageName: 'Menu Setup',
        auth: { permission: 'view_menu_setup' }
    },
    {
        path: '/settings/menu-setup/add',
        element: loadComponent('./pages/Settings/MenuSetup/Add', 'add'),
        layout: 'main',
        pageName: 'Add Menu',
        auth: { permission: 'manage_menu_setup' }
    },

    {
        path: '/settings/menu-setup/edit/:id',
        element: loadComponent('./pages/Settings/MenuSetup/Edit', 'edit'),
        layout: 'main',
        pageName: 'Edit Menu',
        auth: { permission: 'manage_menu_setup' }
    },

    // My Settings / User Settings
    {
        path: '/settings/user-settings',
        element: loadComponent('./pages/Settings/UserSettings'),
        layout: 'main',
        pageName: 'User Settings',
        auth: { permission: 'view_user_settings' }
    },
    // System Settings
    {
        path: '/settings/system-settings',
        element: loadComponent('./pages/Settings/SystemSettings/index', 'list'),
        layout: 'main',
        pageName: 'System Settings',
        auth: { permission: 'view_system_settings' }
    },
    {
        path: '/settings/system-settings/add',
        element: loadComponent('./pages/Settings/SystemSettings/Add', 'add'),
        layout: 'main',
        pageName: 'Add System Settings',
        auth: { permission: 'manage_system_settings' }
    },
    {
        path: '/settings/system-settings/edit/:id',
        element: loadComponent('./pages/Settings/SystemSettings/Edit', 'edit'),
        layout: 'main',
        pageName: 'Edit System Setting',
        auth: { permission: 'manage_system_settings' }
    },

    // API Settings
    {
        path: '/settings/API-settings',
        element: loadComponent('./pages/Settings/ApiSettings/index', 'list'),
        layout: 'main',
        pageName: 'API Settings',
        auth: { permission: 'view_system_settings' }
    },
    {
        path: '/settings/API-settings/add',
        element: loadComponent('./pages/Settings/ApiSettings/Add', 'add'),
        layout: 'main',
        pageName: 'Add API Settings',
        auth: { permission: 'manage_system_settings' }
    },
    {
        path: '/settings/API-settings/edit/:id',
        element: loadComponent('./pages/Settings/ApiSettings/Edit', 'edit'),
        layout: 'main',
        pageName: 'Edit API Settings',
        auth: { permission: 'manage_system_settings' }
    },

    // Database Documentation
    {
        path: '/documentation/database-doc',
        element: loadComponent('./pages/DatabaseDoc/index', 'list'),
        layout: 'main',
        pageName: 'Database Documentation List',
        auth: { permission: 'view_database_documentation' }
    },

    // --- Not Found Route (Must be last within its scope, do not add routes below) ---
    { path: '*', element: NotFound, layout: 'main', pageName: 'Not Found' }
];

export default routeConfig;
